import {
  ArrowRightIcon,
  ShareIcon,
  ShuffleIcon,
  UsersIcon,
  WordsHappenButton,
  WordsHappenFormAlert,
} from '@words-happen/ui-components';
import type React from 'react';
import { LobbyFrame } from './lobby-frame';
import styles from './lobby-screen.module.scss';

/**
 * - `choose`: invite a friend or quick match; `pending` while that request is being sent.
 * - `invite`: the game is waiting, and this is its invitation. `copied` once the link has been copied.
 * - `waiting-for-match`: quick match found nobody waiting, so the caller's game waits for the next player.
 * - `matched`: quick match seated the caller in another player's game, which has started.
 * - `too-many-games`: the server refused, as the caller is in as many games as they may be. `message` is its reason;
 *   `nextGame` is a game where it is their turn, or null while that is being looked up or there is none.
 */
export type NewGameScreenState =
  | { kind: 'choose'; pending?: 'invite' | 'quick-match' }
  | { kind: 'too-many-games'; message: string; nextGame: { opponentName: string } | null }
  | { kind: 'invite'; code: string; link: string; copied: boolean }
  | { kind: 'waiting-for-match' }
  | { kind: 'matched'; opponentName: string };

export interface NewGameScreenProps {
  state: NewGameScreenState;
  /** Why the last request failed, as the player should read it. */
  error?: string;
  onInvite: () => void;
  onQuickMatch: () => void;
  onCopyLink: () => void;
  /** Opens the device's share sheet with the invite link. Leave out where the browser can't share. */
  onShare?: () => void;
  /** Goes to the games list. */
  onShowGames: () => void;
  /** Opens `too-many-games`' next game. */
  onPlayNext: () => void;
  onBack: () => void;
  className?: string;
}

const Choose: React.FC<
  { pending?: 'invite' | 'quick-match'; disabled?: boolean } & Pick<NewGameScreenProps, 'onInvite' | 'onQuickMatch'>
> = ({ pending, disabled = pending !== undefined, onInvite, onQuickMatch }) => (
  <>
    <div className={styles.card}>
      <p className={styles.lead}>Send a friend a link or code. The game starts when they join.</p>
      <WordsHappenButton
        variant="primary"
        icon={<UsersIcon />}
        loading={pending === 'invite'}
        disabled={disabled}
        onClick={onInvite}
      >
        Invite a friend
      </WordsHappenButton>
    </div>
    <div className={styles.card}>
      <p className={styles.lead}>Play the next player who is looking for a game.</p>
      <WordsHappenButton
        variant="sun"
        icon={<ShuffleIcon />}
        loading={pending === 'quick-match'}
        disabled={disabled}
        onClick={onQuickMatch}
      >
        Quick match
      </WordsHappenButton>
    </div>
  </>
);

const Invitation: React.FC<
  { code: string; link: string; copied: boolean } & Pick<NewGameScreenProps, 'onCopyLink' | 'onShare'>
> = ({ code, link, copied, onCopyLink, onShare }) => (
  <div className={styles.card}>
    <p className={styles.lead}>Send this link to a friend, or read them the code. The game starts when they join.</p>
    <p className={styles.code} aria-label={`Invite code ${code.split('').join(' ')}`}>
      {code}
    </p>
    <p className={styles.link}>{link}</p>
    {onShare ? (
      <WordsHappenButton variant="primary" icon={<ShareIcon />} onClick={onShare}>
        Share invite link
      </WordsHappenButton>
    ) : null}
    <WordsHappenButton variant={onShare ? 'default' : 'primary'} onClick={onCopyLink}>
      {copied ? 'Link copied' : 'Copy invite link'}
    </WordsHappenButton>
    <span className={styles.status} role="status">
      {copied ? 'The invite link is on your clipboard.' : ''}
    </span>
  </div>
);

/** The way on from the game limit: the player's turn in a game they have, or else their games, to finish or cancel one. */
const TooManyGames: React.FC<
  { message: string; nextGame: { opponentName: string } | null } & Pick<
    NewGameScreenProps,
    'onPlayNext' | 'onShowGames' | 'onInvite' | 'onQuickMatch'
  >
> = ({ message, nextGame, onPlayNext, onShowGames, onInvite, onQuickMatch }) => (
  <>
    <WordsHappenFormAlert>{message}</WordsHappenFormAlert>
    <div className={styles.card}>
      {nextGame ? (
        <>
          <p className={styles.lead}>{`It's your turn against ${nextGame.opponentName}.`}</p>
          <WordsHappenButton variant="primary" icon={<ArrowRightIcon />} onClick={onPlayNext}>
            {`Play your turn against ${nextGame.opponentName}`}
          </WordsHappenButton>
        </>
      ) : (
        <>
          <p className={styles.lead}>Finish a game or cancel one that's waiting, then start another.</p>
          <WordsHappenButton variant="primary" onClick={onShowGames}>
            Go to your games
          </WordsHappenButton>
        </>
      )}
    </div>
    <Choose disabled onInvite={onInvite} onQuickMatch={onQuickMatch} />
  </>
);

const Outcome: React.FC<{ text: string }> = ({ text }) => (
  <div className={styles.card}>
    <p className={styles.lead} role="status">
      {text}
    </p>
  </div>
);

/**
 * The new-game screen (/games/new): invite a friend, or quick match. It holds no state: the app sends the request and
 * passes in what came back. No state is a dead end: each offers a next step beyond the back control.
 */
export const NewGameScreen: React.FC<NewGameScreenProps> = ({
  state,
  error,
  onInvite,
  onQuickMatch,
  onCopyLink,
  onShare,
  onShowGames,
  onPlayNext,
  onBack,
  className,
}) => (
  <LobbyFrame title="New game" backLabel="Back to your games" onBack={onBack} className={className}>
    {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}
    {state.kind === 'choose' ? (
      <Choose pending={state.pending} onInvite={onInvite} onQuickMatch={onQuickMatch} />
    ) : null}
    {state.kind === 'too-many-games' ? (
      <TooManyGames
        message={state.message}
        nextGame={state.nextGame}
        onPlayNext={onPlayNext}
        onShowGames={onShowGames}
        onInvite={onInvite}
        onQuickMatch={onQuickMatch}
      />
    ) : null}
    {state.kind === 'invite' ? (
      <Invitation code={state.code} link={state.link} copied={state.copied} onCopyLink={onCopyLink} onShare={onShare} />
    ) : null}
    {state.kind === 'waiting-for-match' ? (
      <Outcome text="Nobody is looking for a game right now. Yours is waiting: the next player to ask joins it." />
    ) : null}
    {state.kind === 'matched' ? <Outcome text={`You're playing ${state.opponentName}.`} /> : null}
    {state.kind === 'choose' || state.kind === 'too-many-games' ? null : (
      <WordsHappenButton onClick={onShowGames}>Go to your games</WordsHappenButton>
    )}
  </LobbyFrame>
);
